import get from 'lodash.get';

// Test that the content object has a teaseImage with a valid url.
export const hasTeaseImage = (content) => {
  let url = false;
  if (content.alternateTeaseImages && content.alternateTeaseImages.length) {
    url = get(content, 'alternateTeaseImages[0].image.url', false);
  }
  if (!url && content.teaseImage) {
    url = get(content, 'teaseImage.url', false);
  }
  return url && url.primary && /^http/.test(url.primary);
};

// Get tease image data from the content object.
export const getTeaseObject = (content) => {
  if (content.alternateTeaseImages && content.alternateTeaseImages.length) {
    // Variable aspect ratios
    return content.alternateTeaseImages;
  }
  if (content.teaseImage) {
    // Legacy
    return content.teaseImage;
  }
  // Not found
  return null;
};

export const getComputedTease = (content, square = false) => {
  const teases = getTeaseObject(content);
  if (Array.isArray(teases)) {
    const ratio = square ? '1X1' : '2X1';
    return teases.find((t) => t.aspectRatio.search(ratio) !== -1);
  }
  return teases;
};

export const getTeaseForRatio = (content, square = false) => {
  const tease = getComputedTease(content, square);
  if (tease && tease.image) {
    return tease.image;
  }
  return tease;
};

export const getTeaseByline = (tease) => {
  if (!tease) {
    return '';
  }

  const { authors, source } = tease;
  const authorName = get(authors, '[0].name') || '';
  const sourceName = get(source, 'name') || '';

  if (authorName && sourceName) {
    return `${authorName} / ${source.name}`;
  }

  return authorName || sourceName;
};

const teaseImageUrl = (tease) => {
  const value = ((tease || {}).url || {}).primary || '';
  return value.replace(/\s/gi, '%20');
};

export const getTeaseUrl = (content, square = false) => {
  if (content.computedValues) {
    const { computedValues } = content;
    let url = null;

    // alternateTeaseImages with variable aspect ratios.
    if (computedValues.alternateTeaseImages && computedValues.alternateTeaseImages.length) {
      const tease = getTeaseForRatio(computedValues, square);
      url = teaseImageUrl(tease);
    }

    // Legacy singular teaseImage.
    if (computedValues.teaseImage && !url) {
      url = teaseImageUrl(computedValues.teaseImage);
    }

    // No image.
    return url;
  }

  // Super legacy tease image.
  const { coverImage, mainImage, type } = content;
  if (type === 'slideshow') {
    const [slide] = content.slides;
    if (!slide.image) {
      return '';
    }
    return (coverImage || mainImage || slide.image || {}).url || '';
  }
  return (coverImage || mainImage || {}).url || '';
};

export const getTeasePicture = (
  computedValues,
  fallbackImageUrlMap,
  vertical = 'news',
  aspectRatio = 'ASPECT_2X1',
) => {
  if (!hasTeaseImage(computedValues)) {
    Object.assign(computedValues, {
      alternateTeaseImages: [
        {
          aspectRatio,
          image: {
            altText: vertical,
            url: {
              primary: fallbackImageUrlMap[vertical.toLowerCase()],
            },
          },
        },
      ],
    });
  }
  return computedValues;
};

export const getDek = (props) => {
  const { card } = props;
  const { computedValues } = card || {};
  const dek = (computedValues && computedValues.dek) || '';

  return dek;
};

export const findPrices = (primaryOffer) => {
  if (!(primaryOffer && primaryOffer.prices && primaryOffer.prices.reduce)) {
    return {};
  }
  return primaryOffer.prices.reduce((accum, current) => ({
    ...accum,
    [current.type]: current.price,
  }),
  {});
};

/**
 * @param  {Object} card
 */
export const getPrimaryOfferPrices = (card) => {
  const offers = get(card, 'offers')
    || (card.item && card.item.offers)
    || [];
  const primaryOffer = offers.find((offer) => offer.isPrimaryOffer);

  if (offers.length) {
    return findPrices(primaryOffer);
  }
  return {};
};

export const getInfoForShopButtons = (props) => {
  const { card } = props;
  const { name: productName, offers, item } = card.item || card;
  const itemOffers = item && item.offers;
  const { externalUrl, seller } = (offers && offers.length && offers[0])
    || (itemOffers && itemOffers.length && itemOffers[0])
    || {};
  const productUrl = (externalUrl || (seller && seller.externalUrl)) || {};
  const { sale, list } = getPrimaryOfferPrices(card);
  return {
    list,
    productName,
    productUrl,
    sale,
    seller,
  };
};

export const getUniformTeaseItem = (item) => {
  const { type } = item;

  // Item from curation
  if (typeof item.computedValues !== 'undefined') {
    return {
      ...item,
      authors: get(item, ['item', 'authors'], []),
      headline: get(item, ['computedValues', 'headline']),
      tease: getTeaseUrl(item),
      type,
      unibrow: get(item, ['computedValues', 'unibrow']),
      url: get(item, ['computedValues', 'url']),
    };
  }

  // Item from content object
  return {
    ...item,
    authors: get(item, 'authors', []),
    headline: get(item, ['headline', 'primary']),
    tease: teaseImageUrl(item.teaseImage),
    type,
    unibrow: get(item, 'unibrow'),
    url: get(item, ['url', 'primary']),
  };
};
